import { useRef, useState, type FormEvent } from "react";
import { Link } from "wouter";
export default function LeadForm() {
  const [status, setStatus] = useState(""),
    [busy, setBusy] = useState(false),
    [done, setDone] = useState(false),
    [preview, setPreview] = useState(false);
  const id = useRef(crypto.randomUUID());
  const sending = useRef(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setStatus("");
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const attribution = Object.fromEntries(
      new URLSearchParams(location.search),
    );
    try {
      const response = await fetch("/api/business-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, requestId: id.current, attribution }),
      });
      const result = await response.json();
      if (!response.ok || !result.success)
        throw new Error(result.message || "Please try again.");
      setPreview(Boolean(result.preview));
      setDone(true);
    } catch (e) {
      setStatus(
        e instanceof Error
          ? e.message
          : "Could not save your request. Please try again.",
      );
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  if (done)
    return (
      <div className="success" role="status">
        <h3>{preview ? "Preview complete." : "Your request is saved."}</h3>
        <p>
          {preview
            ? "This is a local test. No lead was saved to the business database and no email was sent."
            : "We’ll review what you need and follow up. Want to talk sooner?"}
        </p>
        <Link href="/book" className="btn">
          Choose a time to talk
        </Link>
      </div>
    );
  return (
    <form className="form" onSubmit={submit}>
      <div className="formrow">
        <label>
          Your name
          <input
            name="name"
            autoComplete="name"
            required
            minLength={2}
            maxLength={150}
          />
        </label>
        <label>
          Work email
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={150}
          />
        </label>
      </div>
      <div className="formrow">
        <label>
          Business name
          <input
            name="company"
            autoComplete="organization"
            required
            maxLength={150}
          />
        </label>
        <label>
          Business type
          <select name="industry" required defaultValue="">
            <option value="" disabled>
              Select your business
            </option>
            <option value="service">Service business</option>
            <option value="restaurant">Restaurant or hospitality</option>
            <option value="yard">Soil, compost, or wholesale</option>
            <option value="other">Another type of business</option>
          </select>
        </label>
      </div>
      <label>
        What would you like help with?
        <textarea
          name="needs"
          rows={4}
          required
          minLength={10}
          maxLength={2500}
          placeholder="More leads? Better follow-up? Email, ads, or a process taking too much time? Tell us what’s happening."
        />
      </label>
      <div className="formrow">
        <label>
          Monthly service budget
          <select name="budget" defaultValue="Not sure yet">
            <option>Not sure yet</option>
            <option>$500–$1,000</option>
            <option>$1,000–$1,500</option>
            <option>$1,500–$3,000</option>
            <option>$3,000+</option>
          </select>
        </label>
        <label>
          Phone (optional)
          <input name="phone" type="tel" autoComplete="tel" maxLength={40} />
        </label>
      </div>
      <label className="trap" aria-hidden="true">
        Leave this blank
        <input name="company_website" tabIndex={-1} autoComplete="off" />
      </label>
      {status && (
        <p className="error" role="alert">
          {status}
        </p>
      )}
      <button className="btn" disabled={busy}>
        {busy ? "Saving your request…" : "Let’s find your starting point"}
      </button>
      <p className="small">
        We’ll contact you about this request. No newsletter signup.{" "}
        <Link href="/privacy">Privacy</Link>.
      </p>
    </form>
  );
}
